import React, { useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MapPinIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import LoadingSpinner from '../Loading/LoadingSpinner';
import AddressModal from './AddressModal';

const AddressList = ({ userId, userType, addresses, setAddresses, showTitle = true }) => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const queryClient = useQueryClient();

    // Mutation pour créer une adresse
    const createMutation = useMutation({
        mutationFn: (newAddress) => {
            return api.post(API.ADMIN_CREATE_ADDRESS, { ...newAddress, userId });
        },
        onSuccess: (response) => {
            // S'assurer qu'on a la nouvelle adresse avec son ID généré par le backend
            const newAddress = response.data;

            // Si c'est la première adresse ou si elle est définie comme principale
            if (newAddress.isPrimary) {
                // Mettre à jour toutes les adresses existantes
                setAddresses(prev => [
                    ...prev.map(addr => ({
                        ...addr,
                        isPrimary: false // Désactiver toutes les adresses existantes
                    })),
                    newAddress // Ajouter la nouvelle adresse (qui est principale)
                ]);
            } else {
                // Simplement ajouter la nouvelle adresse sans changer les autres
                setAddresses(prev => [...prev, newAddress]);
            }

            // Invalider les requêtes
            queryClient.invalidateQueries(['addresses', userId]);
            queryClient.invalidateQueries(['therapists']);

            setIsCreateModalOpen(false);
        }
    });

    // Mutation pour mettre à jour une adresse
    const updateMutation = useMutation({
        mutationFn: (updatedAddress) => {
            return api.patch(`${API.ADMIN_UPDATE_ADDRESS}/${updatedAddress.id}`, updatedAddress);
        },
        onSuccess: (response) => {
            // Problème potentiel : vérifiez si response.data contient bien l'adresse mise à jour
            // Si ce n'est pas le cas, utilisez les données envoyées à la mutation
            const updatedAddress = response.data || updatedAddress;
            
            // Mettre à jour l'état local immédiatement
            setAddresses(prev => prev.map(addr => addr.id === updatedAddress.id ? updatedAddress : addr));

            // Puis invalider les requêtes
            queryClient.invalidateQueries(['addresses', userId]);
            queryClient.invalidateQueries(['therapists']);

            setIsEditModalOpen(false);
            setSelectedAddress(null);
        }
    });

    // Mutation pour supprimer une adresse
    const deleteMutation = useMutation({
        mutationFn: (addressId) => {
            return api.delete(`${API.ADMIN_DELETE_ADDRESS}/${addressId}`);
        },
        onSuccess: (_, addressId) => {
            // Mettre à jour l'état local en supprimant l'adresse
            setAddresses(prev => prev.filter(addr => addr.id !== addressId));

            // Invalider les requêtes pour synchroniser avec le backend
            queryClient.invalidateQueries(['addresses', userId]);
            queryClient.invalidateQueries(['therapists']);
        }
    });

    // Mutation pour définir une adresse comme principale
    const setPrimaryMutation = useMutation({
        mutationFn: async (addressId) => {
            // Trouver l'adresse principale actuelle
            const currentPrimaryAddress = addresses.find(addr => addr.isPrimary);

            // Si une adresse principale existe et est différente
            if (currentPrimaryAddress && currentPrimaryAddress.id !== addressId) {
                // D'abord désactiver l'ancienne adresse principale
                await api.patch(`${API.ADMIN_UPDATE_ADDRESS}/${currentPrimaryAddress.id}`, {
                    isPrimary: false
                });
            }

            // Puis activer la nouvelle adresse principale
            return api.patch(`${API.ADMIN_UPDATE_ADDRESS}/${addressId}`, {
                isPrimary: true
            });
        },
        onSuccess: (response, addressId) => {
            // Mettre à jour l'état local immédiatement
            setAddresses(prev =>
                prev.map(addr => ({
                    ...addr,
                    // Définir isPrimary à true seulement pour l'adresse choisie
                    isPrimary: addr.id === addressId
                }))
            );

            // Invalider les requêtes
            queryClient.invalidateQueries(['addresses', userId]);
            queryClient.invalidateQueries(['therapists']);
        }
    });

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                {showTitle && (
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <MapPinIcon className="h-5 w-5 text-primary" />
                        Adresses
                    </h3>
                )}
                <button
                    className="btn btn-primary btn-sm"
                    type='button'
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsCreateModalOpen(true);
                    }}
                >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Ajouter une adresse
                </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
                {addresses?.map((address) => (
                    <div
                        key={address.id}
                        className={`card relative bg-base-100 border ${address.isPrimary ? 'border-primary border-2' : 'border-base-200'} shadow-sm rounded-lg overflow-hidden transition-all hover:shadow-md`}
                    >
                        <div className="card-body p-4">
                            <div className="flex justify-between items-start">
                                <div className="flex gap-3">
                                    <div className="pt-1">
                                        <MapPinIcon className={`h-5 w-5 ${address.isPrimary ? 'text-primary' : 'text-base-content/70'}`} />
                                    </div>
                                    <div>
                                        {(address.legalFirstname || address.legalLastname) && (
                                            <h3 className="font-semibold">
                                                {address.legalFirstname} {address.legalLastname}
                                            </h3>
                                        )}
                                        <p className="text-sm">{address.street}</p>
                                        <p className="text-sm">
                                            {address.postcode} {address.city}
                                        </p>
                                        <p className="text-sm text-base-content/70">{address.country}</p>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        className="btn btn-ghost btn-sm btn-square"
                                        type='button'
                                        aria-label="Modifier"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedAddress(address);
                                            setIsEditModalOpen(true);
                                        }}
                                    >
                                        <PencilIcon className="h-4 w-4" />
                                    </button>
                                    {!address.isPrimary && (
                                        <button
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                            type='button'
                                            aria-label="Supprimer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (confirm('Êtes-vous sûr de vouloir supprimer cette adresse ?')) {
                                                    deleteMutation.mutate(address.id);
                                                }
                                            }}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            <div className="mt-2">
                                {address.isPrimary ? (
                                    <div className="badge badge-primary gap-1">
                                        <CheckCircleIcon className="h-3 w-3" />
                                        Adresse principale
                                    </div>
                                ) : (
                                    <button
                                        type='button'
                                        className="btn btn-ghost btn-sm text-primary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (confirm('Définir cette adresse comme principale ?')) {
                                                setPrimaryMutation.mutate(address.id);
                                            }
                                        }}
                                    >
                                        Définir comme principale
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {addresses?.length === 0 && (
                <div className="text-center py-6 border border-dashed border-base-300 rounded-lg bg-base-100">
                    <MapPinIcon className="h-10 w-10 text-base-content/30 mx-auto mb-2" />
                    <p className="text-base-content/70">Aucune adresse enregistrée</p>
                    <button
                        className="btn btn-primary btn-sm mt-3"
                        type='button'
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Ajouter une adresse
                    </button>
                </div>
            )}

            <AddressModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={createMutation.mutate}
                isLoading={createMutation.isPending}
                address={null}
                mode="create"
            />

            <AddressModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedAddress(null);
                }}
                onSubmit={updateMutation.mutate}
                isLoading={updateMutation.isPending}
                address={selectedAddress}
                mode="edit"
            />
        </div>
    );
};

export default AddressList; 